/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   snapshotsApi.ts
 *     Description     :   API service for infrastructure snapshot trends and history
 *     Author          :   Peter Dobler
 *     Created         :   2025-01-21
 *     Modified        :   2025-01-21 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.12.0 (Production Integration Edition)
 *     Feature         :   021-infrastructure-data-collection (T030)
 * =========================================================================================
 */

import { apiClient } from './apiClient';
import type { CapacityTypeValue } from '@shared/types/entities';
import { CAPACITY_TYPE_LABELS } from '@shared/types/client-detail';

// API base path for infrastructure snapshot endpoints
const API_PATH = '/api/infrastructure-snapshots';

// ============================================================================
// Types
// ============================================================================

export type SnapshotType =
  | 'capacity'
  | 'workspace'
  | 'lakehouse'
  | 'warehouse'
  | 'pipeline'
  | 'notebook'
  | 'report'
  | 'semantic_model'
  | 'storage_account';

export type TimeRange = 7 | 30 | 90 | 365;

/**
 * Spec 179's SKU taxonomy, the values trends.controller.ts VALID_CAPACITY_TYPES accepts. The enterprise names
 * (Platform, Reference, ClientDedicated, MultiTenant) were retired there and the API answered 400 to every one of
 * them (the owner, 2026-11-02, on dev), while this file still offered them.
 */
export type CapacityType = CapacityTypeValue;
export const CAPACITY_TYPES: readonly CapacityType[] = ['Fabric', 'PowerBI', 'MsManaged', 'Unknown'];
/** The options every capacity-type filter of Infrastructure Trends offers, labelled from the shared map. */
export const CAPACITY_TYPE_OPTIONS: ReadonlyArray<{ value: CapacityType; label: string }> =
  CAPACITY_TYPES.map((value) => ({ value, label: CAPACITY_TYPE_LABELS[value] }));

export interface TrendQueryParams {
  time_range?: TimeRange;
  capacity_type?: CapacityType;
  capacity_id?: string;
  item_id?: string;
  limit?: number;
}

// ============================================================================
// Response Types
// ============================================================================

export interface TrendItem {
  item_id: string;
  item_name: string;
  latest_value: number;
  avg_value: number;
  min_value: number;
  max_value: number;
  data_point_count: number;
  first_seen: string;
  last_seen: string;
  /** Null when the first measured point is 0: no percentage change from nothing (Spec 298 FR-004). */
  change_percent: number | null;
}

export interface TrendsResponse {
  snapshot_type: SnapshotType;
  time_range: TimeRange;
  items: TrendItem[];
  /** Items with no measured day in the period; not in `items`. */
  not_measured: Array<{ item_id: string; item_name: string }>;
  count: number;
}

/** A capacity's name is TrendItem.item_name; the API sends no capacity_name on a trend row. */
export interface CapacityUtilizationTrend extends TrendItem {
  sku: string;
  capacity_type: CapacityType;
}

export interface CapacityUtilizationResponse {
  time_range: TimeRange;
  capacity_type?: CapacityType;
  capacities: CapacityUtilizationTrend[];
  /** Capacities with no utilization telemetry in the period; not in `capacities` or the summary. */
  not_measured: Array<{ capacity_id: string; capacity_name: string }>;
  count: number;
  summary: {
    avg_utilization: number;
    max_utilization: number;
    over_80_percent_count: number;
  };
}

export interface LakehouseStorageTrend extends TrendItem {
  lakehouse_name: string;
  workspace_name: string;
  growth_rate_daily_gb: number;
}

export interface LakehouseStorageResponse {
  time_range: TimeRange;
  lakehouses: LakehouseStorageTrend[];
  count: number;
  summary: {
    total_storage_gb: number;
    avg_daily_growth_gb: number;
    high_growth_count: number;
  };
}

export interface PipelineHealthTrend {
  item_id: string;
  pipeline_name: string;
  workspace_name: string;
  total_runs: number;
  success_count: number;
  failure_count: number;
  avg_success_rate: number | null;   // null with no measured day / pipeline (Spec 298 FR-004)
  avg_duration_seconds: number;
  first_seen: string;
  last_seen: string;
}

export interface PipelineHealthResponse {
  time_range: TimeRange;
  pipelines: PipelineHealthTrend[];
  count: number;
  summary: {
    avg_success_rate: number | null;
    total_runs: number;
    total_failures: number;
    low_success_count: number;
  };
}

export interface HistoryDataPoint {
  snapshot_date: string;
  value: number;
  metadata?: Record<string, unknown>;
}

export interface ItemHistoryResponse {
  snapshot_type: SnapshotType;
  item_id: string;
  time_range: TimeRange;
  data_points: HistoryDataPoint[];
  count: number;
}

export interface CollectionRun {
  data_collection_run_id: string;
  status: 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at?: string;
  items_collected: number;
  error_message?: string;
}

export interface CollectionRunsResponse {
  runs: CollectionRun[];
  count: number;
}

// ============================================================================
// API Functions - Trends
// ============================================================================

/**
 * The generic trend and history routes no longer serve `capacity` (retired 2026-10-18); capacity
 * utilization is getCapacityUtilizationTrends.
 */
export type TrendSnapshotType = Exclude<SnapshotType, 'capacity'>;

/**
 * Get generic trend data for any snapshot type except capacity
 *
 * @param snapshotType - The type of snapshot to query
 * @param params - Optional query parameters
 * @returns Trend data for the specified snapshot type
 */
export async function getTrends(
  snapshotType: TrendSnapshotType,
  params?: TrendQueryParams
): Promise<TrendsResponse> {
  const response = await apiClient.get(`${API_PATH}/trends/${snapshotType}`, {
    params,
  });
  return response.data;
}

/**
 * Get capacity utilization trends with summary metrics
 * 
 * @param params - Optional query parameters
 * @returns Capacity utilization trends with summary
 */
export async function getCapacityUtilizationTrends(
  params?: TrendQueryParams
): Promise<CapacityUtilizationResponse> {
  const response = await apiClient.get(`${API_PATH}/trends/capacity/utilization`, {
    params,
  });
  return response.data;
}

/**
 * Get lakehouse storage trends with growth rate
 * 
 * @param params - Optional query parameters
 * @returns Lakehouse storage trends with growth summary
 */
export async function getLakehouseStorageTrends(
  params?: TrendQueryParams
): Promise<LakehouseStorageResponse> {
  const response = await apiClient.get(`${API_PATH}/trends/lakehouse/storage`, {
    params,
  });
  return response.data;
}

/**
 * Get pipeline health trends with success/failure metrics
 * 
 * @param params - Optional query parameters
 * @returns Pipeline health trends with summary
 */
export async function getPipelineHealthTrends(
  params?: TrendQueryParams
): Promise<PipelineHealthResponse> {
  const response = await apiClient.get(`${API_PATH}/trends/pipeline/health`, {
    params,
  });
  return response.data;
}

/**
 * Get detailed history for a specific item
 * 
 * @param snapshotType - The type of snapshot
 * @param itemId - The item ID to get history for
 * @param params - Optional query parameters
 * @returns Item history with data points
 */
export async function getItemHistory(
  snapshotType: TrendSnapshotType,
  itemId: string,
  params?: TrendQueryParams
): Promise<ItemHistoryResponse> {
  const response = await apiClient.get(`${API_PATH}/history/${snapshotType}/${itemId}`, {
    params,
  });
  return response.data;
}

// ============================================================================
// API Functions - Collection
// ============================================================================

/**
 * Get collection run history
 * 
 * @param limit - Maximum number of runs to return (default: 50, max: 100)
 * @param offset - Number of runs to skip (default: 0)
 * @returns Collection run history
 */
export async function getCollectionRuns(
  limit?: number,
  offset?: number
): Promise<CollectionRunsResponse> {
  const response = await apiClient.get(`${API_PATH}/collection/runs`, {
    params: { limit, offset },
  });
  return response.data;
}

/**
 * Get specific collection run details
 * 
 * @param runId - The collection run ID
 * @returns Collection run details
 */
export async function getCollectionRunById(
  runId: string
): Promise<CollectionRun> {
  const response = await apiClient.get(`${API_PATH}/collection/runs/${runId}`);
  return response.data;
}

/**
 * Trigger manual collection (admin only)
 * 
 * @returns The new collection run ID
 */
export async function triggerCollection(): Promise<{ collection_run_id: string }> {
  const response = await apiClient.post(`${API_PATH}/collection/run`);
  return response.data;
}

// ============================================================================
// API Functions - Anomalies (Change Detection)
// ============================================================================

export type ChangeType = 'storage_growth' | 'pipeline_failure' | 'deleted_item';
export type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface Anomaly {
  id: string;
  change_type: ChangeType;
  severity: Severity;
  item_id: string;
  item_name: string;
  item_type: string;
  capacity_id: string | null;
  capacity_name: string | null;
  capacity_type: CapacityType | null;
  workspace_id: string | null;
  workspace_name: string | null;
  previous_value?: number | string;
  current_value?: number | string;
  change_percent?: number;
  message: string;
  blast_radius?: string;
  detected_at: string;
}

export interface AnomaliesResponse {
  anomalies: Anomaly[];
  count: number;
  time_range_days: number;
}

export interface AnomaliesQueryParams {
  days?: number;
  severity?: Severity;
  capacity_type?: CapacityType;
  change_type?: ChangeType;
}

/**
 * Get detected anomalies (from alerts table with alert_type='ChangeDetection')
 * 
 * @param params - Query parameters
 * @returns Anomalies list
 */
export async function getAnomalies(
  params?: AnomaliesQueryParams
): Promise<AnomaliesResponse> {
  // Spec 292 T039 (audit F7): the route reads `source`, `severity`, `capacityType`, `acknowledged`, `limit`,
  // `offset` (alertsController.getAlerts) — never `alert_type` or `days`, which this call used to send. Note for
  // the owner (research R-6): the aggregation service drops `source` too, so the route answers its most recent
  // alerts of every type; making it honour the filter is a route-contract change for his decision.
  const response = await apiClient.get('/api/alerts', {
    params: {
      source: 'ChangeDetection',
      severity: params?.severity,
      capacity_type: params?.capacity_type,
    },
  });

  // Transform alerts (the shape alertAggregationService sends: id, severity lower-case, source, timestamp, metadata)
  // to the anomalies format
  const SEVERITY: Record<string, Severity> = { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', low: 'LOW', info: 'LOW' };
  const alerts = response.data.alerts || response.data || [];
  const anomalies: Anomaly[] = alerts.map((alert: Record<string, unknown>) => {
    const metadata = typeof alert.metadata === 'string'
      ? JSON.parse(alert.metadata)
      : alert.metadata || {};

    return {
      id: String(alert.id ?? alert.alert_id ?? ''),
      change_type: metadata.change_type || 'storage_growth',
      severity: SEVERITY[String(alert.severity ?? '').toLowerCase()] ?? 'MEDIUM',
      item_id: alert.item_id || '',
      item_name: typeof alert.title === 'string' ? alert.title.replace(/^(Storage Growth Alert|Pipeline Failure|Item Deleted): /, '') : 'Unknown',
      item_type: alert.item_type || 'unknown',
      capacity_id: alert.capacity_id,
      capacity_name: alert.capacity_name,
      capacity_type: alert.capacity_type,
      workspace_id: alert.workspace_id,
      workspace_name: null,
      previous_value: metadata.previous_value,
      current_value: metadata.current_value,
      change_percent: metadata.change_percent,
      message: alert.message || '',
      blast_radius: metadata.blast_radius,
      detected_at: String(alert.timestamp ?? alert.created_at ?? ''),
    };
  });

  return {
    anomalies,
    count: anomalies.length,
    time_range_days: params?.days || 7,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format bytes to human-readable string
 */
export function formatStorageSize(gb: number): string {
  if (gb >= 1024) {
    return `${(gb / 1024).toFixed(2)} TB`;
  }
  return `${gb.toFixed(2)} GB`;
}

/**
 * Format percentage with color indicator
 */
export function getUtilizationStatus(percent: number): 'ok' | 'warning' | 'critical' {
  if (percent >= 90) return 'critical';
  if (percent >= 80) return 'warning';
  return 'ok';
}

/**
 * Calculate growth rate as percentage per day
 */
export function calculateDailyGrowthRate(
  currentValue: number,
  previousValue: number,
  days: number
): number {
  if (previousValue === 0 || days === 0) return 0;
  const totalGrowth = ((currentValue - previousValue) / previousValue) * 100;
  return totalGrowth / days;
}

// Export all as default object for convenience
export default {
  getTrends,
  getCapacityUtilizationTrends,
  getLakehouseStorageTrends,
  getPipelineHealthTrends,
  getItemHistory,
  getCollectionRuns,
  getCollectionRunById,
  triggerCollection,
  getAnomalies,
  formatStorageSize,
  getUtilizationStatus,
  calculateDailyGrowthRate,
};
